import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getClientById, updateClient, createClient, logError, createClientAddress, updateClientAddress } from '../api';  // Asegúrate de tener estas funciones en api.js
import ClientInfoForm from './ClientInfoForm.js';  // Componente para la información del cliente
import ClientAddressList from './ClientAddressList';  // Componente para las direcciones
import EditCancelModal from './Global/EditCancelModal';

const ClientForm = () => {
    const [client, setClient] = useState(null);  // Inicializar como null
    const [isEditing, setIsEditing] = useState(false); // Para determinar si estamos en edición
    const [isNew, setIsNew] = useState(false); // Para determinar si estamos creando un nuevo cliente
    const [isDirty, setIsDirty] = useState(false);  // Track unsaved changes
    const [showWarning, setShowWarning] = useState(false); // Show warning on cancel
    const [successMessage, setSuccessMessage] = useState('');  // Mensaje de éxito al crear
    const navigate = useNavigate();
    const { id } = useParams();  // Obtener ID del cliente

    useEffect(() => {
        if (id) {
            // Si hay un id, estamos editando un cliente, obtenemos los datos del cliente
            setIsEditing(true);
            setIsNew(false);  
            getClientById(id)
                .then(response => {
                    setClient(response);  // Establecer los datos del cliente
                })
                .catch(error => {
                    console.error('Error fetching client data:', error);
                    logError(error);
                    setClient(null);  // Si hay un error, se asegura de que client sea null
                });
        } else {
            // Si no hay id, estamos creando un nuevo cliente
            setClient({ 
                dni: '', 
                name: '', 
                email: '', 
                phone: '', 
                lastName1: '', 
                lastName2: '', 
                addresses: []  // Asegúrate de que `addresses` sea un array vacío
            });
            setIsEditing(true);  // No estamos en modo edición
            setIsNew(true);  // Estamos creando un nuevo cliente
        }
    }, [id]);

    const handleCancel = () => {
        if (isDirty) {
            setShowWarning(true);
        } else {
            // Si estamos en modo edición y no queremos volver al new, volvemos más atrás en el historial
            navigate(-1);
        }
    };

    const handleSubmit = async (clientData, newAddress) => {
    try {
        if (isNew) {
            // Crear cliente
            const clientResponse = await createClient(clientData);
            const createdClient = clientResponse.data;

            let addresses = [];

            if (newAddress) {
                const addressData = {
                    ...newAddress,
                    clientId: createdClient.id,
                    isDefault: true
                };

                const addressResponse = await createClientAddress(addressData);
                addresses = [addressResponse.data];
            }

            // Actualizamos estado
            const fullClient = {
                ...createdClient,
                addresses
            };

            setClient(fullClient);
            setIsNew(false);
            setIsEditing(true);
            setIsDirty(false);
            setSuccessMessage('Cliente creado exitosamente!');
        } else {
            // Actualizar cliente
            await updateClient(client.id, clientData);

            let updatedAddresses = client.addresses || [];

            if (newAddress) {
                const existingAddress = client.addresses?.find(addr => addr.isDefault);

                if (existingAddress && existingAddress.id) {
                    // Actualizar dirección existente
                    await updateClientAddress(existingAddress.id, {
                        ...newAddress,
                        clientId: client.id,
                        id: existingAddress.id,
                        isDefault: true
                    });

                    updatedAddresses = updatedAddresses.map(addr =>
                        addr.id === existingAddress.id
                            ? { ...newAddress, id: existingAddress.id, clientId: client.id, isDefault: true }
                            : addr
                    );
                } else {
                    // Crear nueva dirección
                    const addressResponse = await createClientAddress({
                        ...newAddress,
                        clientId: client.id,
                        isDefault: true
                    });
                    updatedAddresses.push(addressResponse.data);
                }
            }

            const updatedClient = {
                ...client,
                ...clientData,
                addresses: updatedAddresses
            };

            setClient(updatedClient);
            setIsDirty(false);
            setIsEditing(false);
            navigate(-1);
        }
    } catch (error) {
        logError(error);
        alert('Hubo un error al guardar los cambios.');
    }
};


    if (!client) {
        return <div>Loading...</div>;  // Si no tenemos cliente, mostramos "Loading..." hasta que se cargue
    }

    return (
        <div className="client-form">
            <EditCancelModal
                show={showWarning}
                onConfirm={() => navigate(-1)}
                onCancel={() => setShowWarning(false)}
            />

            {successMessage && (
                <div className="alert alert-success">
                    {successMessage}
                </div>
            )}

            {/* Formulario del cliente */}
            <ClientInfoForm 
                client={client}
                isEditing={isEditing}
                handleSubmit={handleSubmit}
                handleCancel={handleCancel}
                setIsDirty={setIsDirty}
            />

            {/* Mostrar lista de direcciones solo si estamos editando un cliente */}
            {!isNew && (
                <ClientAddressList 
                    client={client}  // Asegúrate de que `client` contenga el `id` actualizado
                    setClient={setClient}
                />
            )}
        </div>
    );
};

export default ClientForm;
